import { debug } from './debug';
/**
 * Locale and Currency Formatting Utilities
 *
 * Provides locale detection and currency/date formatting.
 * Formatting locale is derived from the user's i18n language + browser region.
 * Timezone-based mapping is kept as a fallback for server-side use.
 */

/**
 * Default region for each supported language.
 * Used when browser region can't be determined.
 */
const LANGUAGE_DEFAULT_REGION = {
  en: 'US',
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  hu: 'HU',
  fi: 'FI',
};

/**
 * Build a formatting locale from the user's i18n language and browser region.
 *
 * @param {string} language - 2-letter i18n language code (e.g. 'de', 'en')
 * @returns {string} BCP 47 locale tag (e.g. 'de-AT', 'en-GB')
 */
export const getFormattingLocale = (language) => {
  const lang = language || 'en';
  const fallbackRegion = LANGUAGE_DEFAULT_REGION[lang] || 'US';

  // Extract region from browser locale (e.g. 'de-AT' → 'AT')
  try {
    const browserLocale = navigator.language || navigator.userLanguage || '';

    // Strip non–BCP 47 locale modifiers (e.g. 'en-US@POSIX', 'de-DE@euro').
    // Some OS/browser configs leak Unix/ICU modifiers into navigator.language,
    // which produce invalid language tags and crash Intl.* APIs.
    const cleaned = browserLocale.split('@')[0];
    const parts = cleaned.split('-');

    if (parts.length >= 2) {
      const candidate = parts[parts.length - 1].toUpperCase();
      // Only accept proper region subtags: 2-letter ISO codes or 3-digit
      // UN M49 codes. Anything else (junk from a malformed locale) falls
      // through to the language's default region.
      const region = /^[A-Z]{2}$/.test(candidate) || /^\d{3}$/.test(candidate)
        ? candidate
        : fallbackRegion;
      return buildValidLocale(lang, region, fallbackRegion);
    }
  } catch (e) {
    // navigator not available (SSR)
  }

  // Fallback to default region for language
  return buildValidLocale(lang, fallbackRegion, fallbackRegion);
};

/**
 * Assemble `${lang}-${region}` and verify it is a valid BCP 47 tag before
 * returning it. If validation fails (defence in depth against an unexpected
 * input), degrade to the language's default region, and finally to the bare
 * language, so a malformed locale can never crash a downstream Intl.* call.
 */
const buildValidLocale = (lang, region, fallbackRegion) => {
  const candidates = [`${lang}-${region}`, `${lang}-${fallbackRegion}`, lang];
  for (const tag of candidates) {
    try {
      Intl.getCanonicalLocales(tag);
      return tag;
    } catch (e) {
      debug.warn(`getFormattingLocale: rejected invalid locale tag "${tag}"`);
    }
  }
  // Intl unavailable or every candidate rejected — best-effort default.
  return `${lang}-${fallbackRegion}`;
};

/**
 * Mapping of IANA timezone identifiers to locale codes
 * Used for currency formatting, date formatting, and number formatting
 */
export const TIMEZONE_TO_LOCALE = {
  // Americas - United States
  'America/New_York': 'en-US',
  'America/Los_Angeles': 'en-US',
  'America/Chicago': 'en-US',
  'America/Denver': 'en-US',
  'America/Phoenix': 'en-US',
  'America/Anchorage': 'en-US',
  'America/Honolulu': 'en-US',

  // Americas - Canada
  'America/Toronto': 'en-CA',
  'America/Vancouver': 'en-CA',
  'America/Montreal': 'fr-CA',
  'America/Edmonton': 'en-CA',
  'America/Winnipeg': 'en-CA',
  'America/Halifax': 'en-CA',

  // Americas - Latin America
  'America/Mexico_City': 'es-MX',
  'America/Sao_Paulo': 'pt-BR',
  'America/Buenos_Aires': 'es-AR',
  'America/Santiago': 'es-CL',
  'America/Bogota': 'es-CO',
  'America/Lima': 'es-PE',

  // Europe - Western
  'Europe/London': 'en-GB',
  'Europe/Dublin': 'en-IE',
  'Europe/Paris': 'fr-FR',
  'Europe/Madrid': 'es-ES',
  'Europe/Lisbon': 'pt-PT',
  'Europe/Amsterdam': 'nl-NL',
  'Europe/Brussels': 'nl-BE',
  'Europe/Luxembourg': 'fr-LU',

  // Europe - Central
  'Europe/Berlin': 'de-DE',
  'Europe/Vienna': 'de-AT',
  'Europe/Zurich': 'de-CH',
  'Europe/Rome': 'it-IT',
  'Europe/Prague': 'cs-CZ',
  'Europe/Warsaw': 'pl-PL',
  'Europe/Budapest': 'hu-HU',

  // Europe - Northern
  'Europe/Stockholm': 'sv-SE',
  'Europe/Oslo': 'nb-NO',
  'Europe/Copenhagen': 'da-DK',
  'Europe/Helsinki': 'fi-FI',
  'Europe/Reykjavik': 'is-IS',

  // Europe - Southern
  'Europe/Athens': 'el-GR',
  'Europe/Bucharest': 'ro-RO',
  'Europe/Sofia': 'bg-BG',

  // Europe - Eastern
  'Europe/Moscow': 'ru-RU',
  'Europe/Kiev': 'uk-UA',
  'Europe/Tallinn': 'et-EE',
  'Europe/Riga': 'lv-LV',
  'Europe/Vilnius': 'lt-LT',

  // Asia - East
  'Asia/Tokyo': 'ja-JP',
  'Asia/Seoul': 'ko-KR',
  'Asia/Shanghai': 'zh-CN',
  'Asia/Hong_Kong': 'zh-HK',
  'Asia/Taipei': 'zh-TW',

  // Asia - Southeast
  'Asia/Singapore': 'en-SG',
  'Asia/Bangkok': 'th-TH',
  'Asia/Jakarta': 'id-ID',
  'Asia/Manila': 'en-PH',
  'Asia/Kuala_Lumpur': 'ms-MY',

  // Asia - South
  'Asia/Dubai': 'ar-AE',
  'Asia/Kolkata': 'en-IN',
  'Asia/Karachi': 'ur-PK',

  // Oceania
  'Australia/Sydney': 'en-AU',
  'Australia/Melbourne': 'en-AU',
  'Australia/Brisbane': 'en-AU',
  'Australia/Perth': 'en-AU',
  'Pacific/Auckland': 'en-NZ',

  // Africa
  'Africa/Johannesburg': 'en-ZA',
  'Africa/Cairo': 'ar-EG',
  'Africa/Lagos': 'en-NG',
  'Africa/Nairobi': 'en-KE',
};

/**
 * Get locale code from timezone identifier
 *
 * @param {string} timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @returns {string} Locale code (e.g., 'de-DE')
 */
export const getLocaleFromTimezone = (timezone) => {
  if (!timezone) {
    debug.warn('No timezone provided, defaulting to en-US');
    return 'en-US';
  }

  const locale = TIMEZONE_TO_LOCALE[timezone];

  if (!locale) {
    debug.warn(`No locale mapping found for timezone: ${timezone}, defaulting to en-US`);
    return 'en-US';
  }

  return locale;
};

/**
 * Format currency amount using merchant's locale and currency
 * Uses Intl.NumberFormat for automatic handling of:
 * - Symbol positioning (before/after)
 * - Thousands separators (comma/period/space)
 * - Decimal separators
 *
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param {string} locale - Locale code (e.g., 'en-US', 'de-DE')
 * @returns {string} Formatted currency string (e.g., '$100.00', '100,00 €')
 */
export const formatCurrency = (amount, currencyCode = 'USD', locale = 'en-US') => {
  if (amount === null || amount === undefined) {
    debug.warn('formatCurrency called with null/undefined amount');
    return formatCurrency(0, currencyCode, locale);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).format(amount);
  } catch (error) {
    console.error(`Error formatting currency: ${error.message}`, {
      amount,
      currencyCode,
      locale
    });
    // Fallback to simple formatting
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

/**
 * Format currency amount using merchant data
 * Convenience wrapper that extracts locale and currency from merchant object
 *
 * @param {number} amount - Amount to format
 * @param {object} merchant - Merchant object with timezone and currency_code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyForMerchant = (amount, merchant) => {
  if (!merchant) {
    debug.warn('formatCurrencyForMerchant called without merchant, using defaults');
    return formatCurrency(amount, 'USD', 'en-US');
  }

  const locale = getLocaleFromTimezone(merchant.timezone);
  const currencyCode = merchant.currency_code || 'USD';

  return formatCurrency(amount, currencyCode, locale);
};

/**
 * Format date using merchant's locale
 *
 * @param {Date|string} date - Date to format
 * @param {string} locale - Locale code
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateWithLocale = (date, locale = 'en-US', options = {}) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };

  try {
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    console.error(`Error formatting date: ${error.message}`, { date, locale });
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format date using merchant's timezone-derived locale
 *
 * @param {Date|string} date - Date to format
 * @param {object} merchant - Merchant object with timezone
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateForMerchant = (date, merchant, options = {}) => {
  if (!merchant) {
    return formatDateWithLocale(date, 'en-US', options);
  }

  const locale = getLocaleFromTimezone(merchant.timezone);
  return formatDateWithLocale(date, locale, options);
};

/**
 * Translate a duration ID (e.g. '6h', '2d', '1w') into a localized label.
 *
 * Requires the i18next `t` function so it always uses the current language.
 * Translation keys: duration.hours, duration.days, duration.weeks (with count).
 *
 * If a non-empty `customLabel` is provided (e.g. resolved from
 * `pricing_rules.duration_label` / `pricing_rule_translations.duration_label`
 * via LanguageContext.getPricingTranslation), it is returned as-is and the
 * i18n fallback is skipped. This lets merchants label e.g. a 24h rental as
 * "1 Day" without changing the underlying duration ID.
 *
 * @param {string} durationId - Duration code like '6h', '2d', '1w'
 * @param {Function} t - i18next translation function
 * @param {string} [customLabel] - Optional merchant-defined label (already
 *   resolved to the current language). When truthy, wins over i18n fallback.
 * @returns {string} Localized label (e.g. "6 Stunden", "2 days", "1 Day")
 */
export const getDurationLabel = (durationId, t, customLabel = null) => {
  if (customLabel && typeof customLabel === 'string' && customLabel.trim() !== '') {
    return customLabel;
  }
  if (!durationId) return '';
  const match = durationId.match(/^(\d+)([hdw])$/);
  if (!match) return durationId;

  const [, count, unit] = match;
  const num = parseInt(count);

  if (unit === 'h') return t('duration.hours', { count: num });
  if (unit === 'd') return t('duration.days', { count: num });
  if (unit === 'w') return t('duration.weeks', { count: num });
  return durationId;
};

/**
 * Get currency symbol for a currency code
 * Uses Intl.NumberFormat to extract the symbol
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} locale - Locale code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'USD', locale = 'en-US') => {
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).format(0);

    // Extract just the symbol by removing digits and common separators
    return formatted.replace(/[\d.,\s]/g, '');
  } catch (error) {
    console.error(`Error getting currency symbol: ${error.message}`);
    return currencyCode;
  }
};
